import React, { useCallback, useContext, useEffect } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Form, useFormikContext } from 'formik'
import { Tabs, Tab } from 'react-bootstrap'
import { last, get } from 'lodash'
import * as Yup from 'yup'
import ConfirmModal from 'components/ConfirmModal'
import RouteLeavingPrompt from 'components/RouteLeavingPrompt'
import { LayoutContext } from 'components/layout/Layout'
import Button from 'components/Button'
import constants from 'constants/constants'
import * as layoutConstants from 'constants/layout'
import { _, colors } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import Editor from './components/Editor'
import validationLocale from './validationLocale'
import { getChangedKeys } from './utils'
import Switch from './components/Switch'
import TabContent from './TabContent'
import { FormTabsProps } from './types'
import TextField from './components/TextField'
import SelectField from './components/SelectField'

Yup.setLocale(validationLocale)
export const S = {
    Form: styled(Form)`
        margin: 0px;
        padding-bottom: 32px;
    `,
    Wrapper: styled.div`
        position: relative;
        padding: 0 10px 20px 10px;
        background: white;
        box-shadow: 0px 1px 4px rgba(0, 0, 0, 0.2);
        border-radius: 4px;
    `,
    Tabs: styled(Tabs)`
        min-width: 0;
        position: relative;
        border: none;
        &::after {
            content: '';
            margin: 0 -10px;
            height: 0;
            position: absolute;
            width: calc(100% + 20px);
            bottom: -1px;
            border-bottom: 1px solid rgba(176, 190, 197, 0.2);
        }
        & > a {
            min-width: 140px;
            max-width: 250px;
            padding-left: 10px;
            padding-right: 10px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            border: none!important;
            font-size: 14px;
            line-height: 16px;
            padding-top: 20px;
            text-transform: uppercase;
            font-weight: 400;
            color: ${colors.grayTitle} !important;
            text-align: center;
            border-bottom: 2px solid transparent !important;
            &[aria-selected="true"] {
                font-weight: 700;
                border-bottom: 2px solid ${colors.grayTitle} !important;
                opacity: 1;
            }
        }
    `,
    TabContent: styled.div`
        display: flex;
        flex-wrap: wrap;
        margin-top: 40px;
    `,
    FormActions: styled.div<{ screenWidth: number, scaledSidebar: number }>`
        position: fixed;
        z-index: 99;
        display: flex;
        align-items: center;
        top: ${layoutConstants.TOP_NAV_HEIGHT}px;
        left: ${({ scaledSidebar }) => scaledSidebar}px;
        height: 42px;
        padding-left: 20px;
        background: #FFFFFF;
        box-shadow: 0px 1px 8px rgba(0, 0, 0, 0.1);
        width: calc(100% -  ${({ scaledSidebar }) => scaledSidebar}px);

        @media (min-width: 1231px) {
            padding-right: ${({ screenWidth }) => Math.min(screenWidth - 1230 + 20, 230)}px;
        }
    `,
    ActionDescription: styled.span`
        font-weight: 300;
        font-size: 16px;
        line-height: 19px;
        color: ${colors.grayDescription};
    `,
    SaveButton: styled(Button)`
        margin-left: auto;
    `,
    SpaceOpened: styled.div`
        position: absolute;
        display: flex;
        align-items: center;
        top: -48px;
        right: 18px;
        font-size: 16px;
        color: ${colors.icon};
        & > div {
            width: 30px;
            margin-top: 3px;
            margin-bottom: 0;
        }
    `
}

const FormTabs = ({
    tabs,
    showHelpContent,
    helpContentField='parent.helpContent',
    showSpaceOpened,
    spaceOpenedField='parent.published',
    showInviteOnly,
    inviteOnlyField='inviteOnly',
    ignoredChangedKeys=[]
}: FormTabsProps) => {
    const dispatch = useDispatch()
    const {
        handleSubmit,
        isValid,
        submitCount,
        values={},
        initialValues={},
        setFieldTouched,
        setFieldValue,
        getFieldMeta,
        isSubmitting,
    } = useFormikContext()
    const { touched: touchedOpenedField } = getFieldMeta(spaceOpenedField)
    const isLiteVersion = constants.isLite

    const isSubmitted = submitCount && isValid
    let changedKeys = getChangedKeys(initialValues, values)
    changedKeys = changedKeys.filter((key) => {
        const value = get(values, key)
        // The quill-editor changes the content automatically in some special case,
        // this causes the changed state of form.
        // Make sure the quill-editor touched, otherwise don't update the changed state of form
        if (typeof value === 'string' && /<\/?[a-z][\s\S]*>/i.test(value)) {
            if (!getFieldMeta(key).touched) {
                return false
            }
        }

        if (ignoredChangedKeys.includes(key)) {
            return false
        }

        return true
    })

    const {
        screenWidth, scaledSidebar, formHasChange, setFormChanged
    } = useContext(LayoutContext)
    useEffect(() => {
        if (!formHasChange && changedKeys.length) {
            setFormChanged(true)
            return
        }

        if (formHasChange && changedKeys.length === 0) {
            setFormChanged(false)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [changedKeys.length, formHasChange])

    const isOpen = get(values, spaceOpenedField)

    const showConfirmModal = useCallback(() => {
        const handleCancel = () => {
            // Reset touched state of field after canceling
            setFieldTouched(spaceOpenedField, false)
            setFieldValue(spaceOpenedField, !isOpen)
        }

        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <ConfirmModal
                    close={close}
                    title={
                        isOpen
                            ? _('spaces.form.open_space_title_modal')
                            : _('spaces.form.close_space_title_modal')
                    }
                    confirmDescription={
                        isOpen
                            ? _('spaces.form.open_space_description_modal')
                            : _('spaces.form.close_space_description_modal')
                    }
                    onCancel={handleCancel}
                    variantConfirm="primary"
                />
            ),
            shouldNotWrap: true
        }))
    // Don't want setFieldTouched and setFieldValue in deps. Because they are changed after submitting
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, isOpen, spaceOpenedField])

    useEffect(() => {
        // Show the confirm modal when open/close space
        if (!isSubmitting && touchedOpenedField) {
            showConfirmModal()
        }
    }, [isSubmitting, showConfirmModal, touchedOpenedField])

    useEffect(() => {
        // Clear invalid indicator on Tabs when form is valid.
        const tabClasses = tabs.map((e) => `a#tab-forms-tab-${e.id}`)
        const validTabs = document.querySelectorAll(tabClasses.join(', '))
        validTabs.forEach((tab) => {
            tab.removeAttribute('data-invalid-tab')
        })

        // Highlight the invalid tabs
        const invalidTabs = document.querySelectorAll('.invalid-field')
        if (invalidTabs.length) {
            invalidTabs.forEach((tab) => {
                const invalidTabPane = tab?.parentNode?.parentNode
                if (invalidTabPane) {
                    // @ts-ignore
                    const invalidTabName = last(invalidTabPane.id.split('-'))
                    const invalidTab =document.getElementById(`tab-forms-tab-${invalidTabName}`)
                    // To show invalid indicator on Tabs when that tab has any invalid field.
                    invalidTab && invalidTab.setAttribute('data-invalid-tab', 'true')
                }
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitCount, isValid])

    return (
        <S.Form>
            <S.Wrapper>
                <S.Tabs defaultActiveKey={tabs[0].id} id="tab-forms">
                    {tabs.map((tab) => (
                        <Tab eventKey={tab.id} title={tab.name} key={tab.id}>
                            <TabContent tab={tab} />
                        </Tab>
                    ))}
                    {showHelpContent && (
                        <Tab eventKey="helpcontent" title="Help Content" key="helpcontent">
                            <S.TabContent className="auto-size-content">
                                <SelectField
                                    name={`${helpContentField}Type`}
                                    label="Help content type"
                                    options={[
                                        { value: 'TEXT', label: 'Text' },
                                        { value: 'VIDEO', label: 'Video' },
                                    ]}
                                    lg={1/2}
                                    isClearable={false}
                                />
                                {get(values, `${helpContentField}Type`) === 'VIDEO' ? (
                                    <TextField
                                        name={helpContentField}
                                        label={_('Embedded Video Url')}
                                    />
                                ) : <Editor name={helpContentField} label="Help content" />}
                            </S.TabContent>
                        </Tab>
                    )}
                </S.Tabs>
                {!isLiteVersion && showSpaceOpened && (
                    <S.SpaceOpened>
                        {get(values, spaceOpenedField) ? _('space_opened') : _('space_closed')}
                        <Switch name={spaceOpenedField} />
                    </S.SpaceOpened>
                )}
                {showInviteOnly && (
                    <S.SpaceOpened>
                        {_('event.invite_only')}
                        <Switch name={inviteOnlyField} />
                    </S.SpaceOpened>
                )}
            </S.Wrapper>
            <RouteLeavingPrompt
                when={!isSubmitted && !!changedKeys.length}
                confirmText="Your change has not been saved"
                submitAction={() => handleSubmit()}
            />
            {!!changedKeys.length && (
                <S.FormActions scaledSidebar={scaledSidebar} screenWidth={screenWidth}>
                    <S.ActionDescription>
                        {_('form.submit_description')}
                    </S.ActionDescription>
                    <S.SaveButton
                        size="sm"
                        disabled={!changedKeys.length}
                        variant="primary"
                        onClick={() => handleSubmit()}
                        icon="save"
                    >
                        {_('form.submit_button')}
                    </S.SaveButton>
                </S.FormActions>
            )}
        </S.Form>
    )
}

export default FormTabs
